import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { 
  Plus, Edit, Trash2
} from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', role: 'staff', 
    permissions: [], first_name: '', last_name: '', phone: '', is_active: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        usersAPI.getUsers(),
        usersAPI.getRolesList()
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const { password, ...dataWithoutPassword } = formData;
        const dataToSend = password ? formData : dataWithoutPassword;
        await usersAPI.updateUser(editingUser.id, dataToSend);
      } else {
        await usersAPI.createUser(formData);
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ username: '', email: '', password: '', role: 'staff', permissions: [], first_name: '', last_name: '', phone: '', is_active: 1 });
      fetchData();
    } catch (error) {
      console.error('Kaydetme hatası:', error);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      permissions: user.permissions || [],
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) {
      try {
        await usersAPI.deleteUser(id);
        fetchData();
      } catch (error) {
        console.error('Silme hatası:', error);
      }
    }
  };

  const handleRoleChange = (roleName) => {
    setFormData({ ...formData, role: roleName });
  };

  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.first_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const roleColors = {
      admin: 'bg-red-100 text-red-800',
      muhasebe: 'bg-green-100 text-green-800',
      satis: 'bg-purple-100 text-purple-800',
      pazarlama: 'bg-pink-100 text-pink-800',
      uretic: 'bg-orange-100 text-orange-800',
      nakliye: 'bg-cyan-100 text-cyan-800',
      stok: 'bg-yellow-100 text-yellow-800',
      insan_kaynaklari: 'bg-indigo-100 text-indigo-800',
      staff: 'bg-gray-100 text-gray-800'
    };
    const roleLabel = roles.find(r => r.name === role)?.label || role;
    return <span className={`px-2 py-1 rounded-full text-xs ${roleColors[role] || 'bg-gray-100'}`}>{roleLabel}</span>;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Kullanıcı Yönetimi</h1>
        <button
          onClick={() => { setEditingUser(null); setFormData({ username: '', email: '', password: '', role: 'staff', permissions: [], first_name: '', last_name: '', phone: '', is_active: 1 }); setShowModal(true); }}
          data-help-target="users-create"
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <Plus size={18} />
          Yeni Kullanıcı
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Kullanıcı ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-4 py-2 border rounded-lg"
        />
      </div>

      {loading ? (
        <div className="text-center py-10">Yükleniyor...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Kullanıcı</th>
                <th className="px-4 py-3 text-left">E-posta</th>
                <th className="px-4 py-3 text-left">Ad Soyad</th>
                <th className="px-4 py-3 text-left">Rol</th>
                <th className="px-4 py-3 text-center">Durum</th>
                <th className="px-4 py-3 text-center">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => (
                <tr key={index} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{user.username}</td>
                  <td className="px-4 py-3">{user.email}</td>
                  <td className="px-4 py-3">{user.first_name} {user.last_name}</td>
                  <td className="px-4 py-3">{getRoleBadge(user.role)}</td>
                  <td className="px-4 py-3 text-center">
                    {user.is_active ? 
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Aktif</span> :
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Pasif</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleEdit(user)} className="text-blue-600 hover:text-blue-800 mr-2"><Edit size={18} /></button>
                    <button onClick={() => handleDelete(user.id)} className="text-red-600 hover:text-red-800"><Trash2 size={18} /></button>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr><td colSpan="6" className="text-center py-8 text-gray-500">Kullanıcı bulunamadı</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-medium mb-1">Kullanıcı Adı *</label><input type="text" required value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">E-posta *</label><input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Şifre {editingUser && '(Değiştirmek için doldurun)'}</label><input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Rol *</label><select required value={formData.role} onChange={e => handleRoleChange(e.target.value)} className="w-full border rounded px-3 py-2"><option value="">Rol Seçin...</option>{roles.map(r => <option key={r.name} value={r.name}>{r.label} - {r.description}</option>)}</select></div>
                <div><label className="block text-sm font-medium mb-1">Ad</label><input type="text" value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Soyad</label><input type="text" value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Telefon</label><input type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Durum</label><select value={formData.is_active} onChange={e => setFormData({...formData, is_active: parseInt(e.target.value)})} className="w-full border rounded px-3 py-2"><option value={1}>Aktif</option><option value={0}>Pasif</option></select></div>
              </div>
              
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded">İptal</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
